import { BaseModel } from './baseModel';
import { Cell } from './cell';
import {
  type CellStyle,
  DEFAULT_CELL_STYLE,
  type RenderedCell,
} from './cellStyle';
import { Col } from './col';
import type { RectRange } from './range';
import { Row } from './row';
import type { Table } from './table';
import { type Unit, computeStyle } from './types';
import { undoManager } from '@/undo';
import { Vector2 } from '@/utils/vec';
import { type WritableAtom, atom } from 'nanostores';

type SheetTransaction<T> = {
  prev: T | undefined;
  next: T | undefined;
};

type TransactionRecord<T> = Record<keyof any, SheetTransaction<T>>;

type TransactionRecords = {
  cells: TransactionRecord<Cell>;
  cols: TransactionRecord<Col>;
  rows: TransactionRecord<Row>;
  tables: TransactionRecord<Table>;
};

export class Sheet extends BaseModel {
  private cells: Record<string, Cell> = {};
  private cellsByCol: Record<number, Record<number, Cell>> = {};
  private cellsByRow: Record<number, Record<number, Cell>> = {};
  private cols: Record<number, Col> = {};
  private rows: Record<number, Row> = {};
  private tables: Record<string, Table> = {};
  public $name = atom<string>('');
  public get name(): string {
    return this.$name.get();
  }
  public set name(name: string) {
    this.$name.set(name);
  }
  // TODO: maybe make the header/footer part of printable tables to allow multiple tables to be printed, each with their own stuff
  public $headerTable = atom<Table | null>(null);
  public get headerTable(): Table | null {
    return this.$headerTable.get();
  }
  public set headerTable(table: Table | null) {
    this.$headerTable.set(table);
  }
  public $mainTable = atom<Table | null>(null);
  public get mainTable(): Table | null {
    return this.$mainTable.get();
  }
  public set mainTable(table: Table | null) {
    this.$mainTable.set(table);
  }
  public $footerTable = atom<Table | null>(null);
  public get footerTable(): Table | null {
    return this.$footerTable.get();
  }
  public set footerTable(table: Table | null) {
    this.$footerTable.set(table);
  }
  public $defaultColWidth = atom<number>(100);
  public get defaultColWidth(): number {
    return this.$defaultColWidth.get();
  }
  public set defaultColWidth(width: number) {
    this.$defaultColWidth.set(width);
  }
  public $defaultRowHeight = atom<number>(30);
  public get defaultRowHeight(): number {
    return this.$defaultRowHeight.get();
  }
  public set defaultRowHeight(height: number) {
    this.$defaultRowHeight.set(height);
  }
  private _cellStores: Map<string, WritableAtom<RenderedCell>> = new Map();
  private _tableStores: Map<string, WritableAtom<Table | null>> = new Map();
  private _transactions: TransactionRecords | null = null;
  // TODO: make a tree so finding visible range is O(log(n)) instead of O(n)

  constructor(name: string) {
    super();
    this.name = name;
  }

  public getCells(): Cell[] {
    return Object.values(this.cells);
  }

  public getCellAt(pos: Vector2): Cell | undefined {
    return this.cells[pos.s];
  }

  public getCellAtOrDefault(pos: Vector2): Cell {
    return this.cells[pos.s] ?? new Cell(pos);
  }

  public setCell(cell: Cell): void {
    if (this.cells[cell.pos.s] === cell) return;
    if (cell.canRemove()) {
      this.removeCellAt(cell.pos);
      return;
    }
    if (this._transactions) {
      this._transactions.cells[cell.pos.s] = {
        prev: this.cells[cell.pos.s],
        ...this._transactions.cells[cell.pos.s],
        next: cell,
      };
    }
    this.cells[cell.pos.s] = cell;
    this.cellsByCol[cell.pos.x] ??= {};
    this.cellsByCol[cell.pos.x]![cell.pos.y] = cell;
    this.cellsByRow[cell.pos.y] ??= {};
    this.cellsByRow[cell.pos.y]![cell.pos.x] = cell;
    this.updateCell(cell.pos);
  }

  public removeCell(cell: Cell | undefined): void {
    if (!cell) return;
    this.removeCellAt(cell.pos);
  }

  public removeCellAt(pos: Vector2): void {
    if (!this.cells[pos.s]) return;
    if (this._transactions) {
      this._transactions.cells[pos.s] = {
        prev: this.cells[pos.s],
        ...this._transactions.cells[pos.s],
        next: undefined,
      };
    }
    delete this.cells[pos.s];
    if (this.cellsByCol[pos.x]) {
      delete this.cellsByCol[pos.x]![pos.y];
    }
    if (this.cellsByRow[pos.y]) {
      delete this.cellsByRow[pos.y]![pos.x];
    }
    this.updateCell(pos);
  }

  public removeCellValueAt(pos: Vector2): void {
    const cell = this.getCellAt(pos);
    if (!cell || cell.value === '') return;
    this.setCell(cell.withValue(''));
  }

  public getCellPos(pos: Vector2): Vector2 {
    return new Vector2(this.getColX(pos.x), this.getRowY(pos.y));
  }

  public getCellSize(pos: Vector2): Vector2 {
    return new Vector2(this.getColWidth(pos.x), this.getRowHeight(pos.y));
  }

  public getCol(index: number): Col | undefined {
    return this.cols[index];
  }

  public getColOrDefault(index: number): Col {
    return this.cols[index] ?? new Col(index);
  }

  public setCol(col: Col): void {
    if (this.cols[col.index] === col) return;
    if (this._transactions) {
      this._transactions.cols[col.index] = {
        prev: this.cols[col.index],
        ...this._transactions.cols[col.index],
        next: col,
      };
    }
    this.cols[col.index] = col;
    this.clearCache(); // TODO: clear col cache
  }

  public removeCol(col: Col): void {
    this.removeColAt(col.index);
  }

  public removeColAt(index: number): void {
    if (!this.cols[index]) return;
    if (this._transactions) {
      this._transactions.cols[index] = {
        prev: this.cols[index],
        ...this._transactions.cols[index],
        next: undefined,
      };
    }
    delete this.cols[index];
    this.clearCache();
  }

  public getColWidth(index: number): number {
    return (
      this.cols[index]?.widthOr(this.defaultColWidth) ?? this.defaultColWidth
    );
  }

  public setColWidth(index: number, width: Unit): void {
    this.setCol(this.getColOrDefault(index).withWidth(width));
  }

  public getCellsByCol(index: number): Iterable<Cell> {
    return Object.values(this.cellsByCol[index] ?? {});
  }

  public getRow(index: number): Row | undefined {
    return this.rows[index];
  }

  public getRowOrDefault(index: number): Row {
    return this.rows[index] ?? new Row(index);
  }

  public setRow(row: Row): void {
    if (this.rows[row.index] === row) return;
    if (this._transactions) {
      this._transactions.rows[row.index] = {
        prev: this.rows[row.index],
        ...this._transactions.rows[row.index],
        next: row,
      };
    }
    this.rows[row.index] = row;
    this.clearCache(); // TODO: clear row cache
  }

  public removeRow(row: Row): void {
    this.removeRowAt(row.index);
  }

  public removeRowAt(index: number): void {
    if (!this.rows[index]) return;
    if (this._transactions) {
      this._transactions.rows[index] = {
        prev: this.rows[index],
        ...this._transactions.rows[index],
        next: undefined,
      };
    }
    delete this.rows[index];
    this.clearCache();
  }

  public getRowHeight(index: number): number {
    return (
      this.rows[index]?.heightOr(this.defaultRowHeight) ?? this.defaultRowHeight
    );
  }

  public setRowHeight(index: number, height: Unit): void {
    this.setRow(this.getRowOrDefault(index).withHeight(height));
  }

  public getCellsByRow(index: number): Cell[] {
    return Object.values(this.cellsByRow[index] ?? {});
  }

  /**
   * Get the index of the column at the given x position in pixels
   * @param x The x position in pixels
   * @returns The index of the column and the accumulated x position
   */
  public getColIndexAt(x: number): { index: number; accum: number } {
    let index = 0;
    let accum = 0;

    for (;;) {
      const colWidth = this.getColWidth(index);

      if (accum + colWidth >= x) return { index, accum };

      accum += colWidth;
      index++;
    }
  }

  /**
   * Get the index of the row at the given y position in pixels
   * @param y The y position in pixels
   * @returns The index of the row and the accumulated y position
   */
  public getRowIndexAt(y: number): { index: number; accum: number } {
    let index = 0;
    let accum = 0;

    for (;;) {
      const rowHeight = this.getRowHeight(index);

      if (accum + rowHeight >= y) return { index, accum };

      accum += rowHeight;
      index++;
    }
  }

  /**
   * Get the x position in pixels of the column at the given index
   * @param index The index of the column
   * @returns The x position in pixels
   */
  public getColX(index: number): number {
    let accum = 0;
    for (let i = 0; i < index; i++) {
      accum += this.getColWidth(i);
    }
    return accum;
  }

  /**
   * Get the y position in pixels of the row at the given index
   * @param index The index of the row
   * @returns The y position in pixels
   */
  public getRowY(index: number): number {
    let accum = 0;
    for (let i = 0; i < index; i++) {
      accum += this.getRowHeight(i);
    }
    return accum;
  }

  public setTable(table: Table): void {
    if (this.tables[table.id] === table) return;
    if (this._transactions) {
      this._transactions.tables[table.id] = {
        prev: this.tables[table.id],
        ...this._transactions.tables[table.id],
        next: table,
      };
    }
    this.tables[table.id] = table;
    this.updateTable(table);
    this.clearCache();
  }

  public removeTable(table: Table): void {
    this.removeTableId(table.id);
  }

  public removeTableId(id: string): void {
    if (!this.tables[id]) return;
    if (this._transactions) {
      this._transactions.tables[id] = {
        prev: this.tables[id],
        ...this._transactions.tables[id],
        next: undefined,
      };
    }
    delete this.tables[id];
    this._tableStores.get(id)?.set(null);
    this._tableStores.delete(id);
    this.clearCache();
  }

  public getTable(id: string): Table | undefined {
    return this.tables[id];
  }

  public getTables(): Table[] {
    return Object.values(this.tables);
  }

  public getUniqueTableName() {
    let i = 1;
    while (this.getTables().find(t => t.name === `Table ${i}`)) {
      i++;
    }
    return `Table ${i}`;
  }

  public getTableAt(pos: Vector2): Table | undefined {
    return this.getTables().find(t => t.range.contains(pos));
  }

  public *getTablesInRange(range: RectRange): Iterable<Table> {
    for (const table of this.getTables()) {
      if (table.range.intersects(range)) {
        yield table;
      }
    }
  }

  public tableStoreAt(pos: Vector2) {
    return this.tableStore(this.getTableAt(pos));
  }

  public tableStore(table: Table | undefined | null) {
    if (!table) return null;
    if (!this._tableStores.has(table.id)) {
      this._tableStores.set(table.id, atom(table));
    }
    return this._tableStores.get(table.id)!;
  }

  private updateTable(table: Table): void {
    this._tableStores.get(table.id)?.set(table);
  }

  public cellStore(pos: Vector2) {
    if (!this._cellStores.has(pos.s)) {
      this._cellStores.set(
        pos.s,
        atom<RenderedCell>(this.getRenderedCellAt(pos)),
      );
    }
    return this._cellStores.get(pos.s)!;
  }

  private updateCell(pos: Vector2): void {
    this._cellStores.get(pos.s)?.set(this.getRenderedCellAt(pos));
  }

  private clearCache(): void {
    this._cellStores.clear();
  }

  public getRenderedCellAt(
    pos: Vector2,
    defaultStyle = DEFAULT_CELL_STYLE,
  ): RenderedCell {
    const cell = this.getCellAt(pos);
    const table = this.getTableAt(pos);
    const col = this.getCol(pos.x);
    const row = this.getRow(pos.y);
    const cellStyle = cell?.style;
    const tableStyle = table?.getStyle(pos);
    const colStyle = col?.cellStyleAt(pos.y);
    const rowStyle = row?.cellStyleAt(pos.x);

    const style: CellStyle = computeStyle(
      defaultStyle,
      tableStyle,
      colStyle,
      rowStyle,
      cellStyle,
    );

    return {
      table,
      cell,
      pos,
      value: cell?.value ?? '',
      style,
    };
  }

  public undoTransaction(transactions: TransactionRecords): void {
    for (const { next, prev } of Object.values(transactions.cells)) {
      const pos = next?.pos ?? prev?.pos;
      if (!prev) this.removeCellAt(pos!);
      else this.setCell(prev);
    }
    for (const { next, prev } of Object.values(transactions.cols)) {
      const index = next?.index ?? prev?.index;
      if (!prev) this.removeColAt(index!);
      else this.setCol(prev);
    }
    for (const { next, prev } of Object.values(transactions.rows)) {
      const index = next?.index ?? prev?.index;
      if (!prev) this.removeRowAt(index!);
      else this.setRow(prev);
    }
    for (const { next, prev } of Object.values(transactions.tables)) {
      const id = next?.id ?? prev?.id;
      if (!prev) this.removeTableId(id!);
      else this.setTable(prev);
    }
  }

  public redoTransaction(transactions: TransactionRecords): void {
    for (const { next, prev } of Object.values(transactions.cells)) {
      const pos = next?.pos ?? prev?.pos;
      if (!next) this.removeCellAt(pos!);
      else this.setCell(next);
    }
    for (const { next, prev } of Object.values(transactions.cols)) {
      const index = next?.index ?? prev?.index;
      if (!next) this.removeColAt(index!);
      else this.setCol(next);
    }
    for (const { next, prev } of Object.values(transactions.rows)) {
      const index = next?.index ?? prev?.index;
      if (!next) this.removeRowAt(index!);
      else this.setRow(next);
    }
    for (const { next, prev } of Object.values(transactions.tables)) {
      const id = next?.id ?? prev?.id;
      if (!next) this.removeTableId(id!);
      else this.setTable(next);
    }
  }

  public startTransaction() {
    if (this._transactions) {
      throw new Error('Transaction already in progress');
    }
    this._transactions = {
      cells: {},
      cols: {},
      rows: {},
      tables: {},
    };

    return {
      commit: (description: string) => this.commitTransaction(description),
      cancel: () => this.cancelTransaction(),
    };
  }

  public cancelTransaction() {
    if (!this._transactions) {
      throw new Error('No transaction in progress');
    }
    this._transactions = null;
  }

  public commitTransaction(description: string) {
    if (!this._transactions) {
      throw new Error('No transaction in progress');
    }

    const transactions = this._transactions;

    undoManager.push({
      undo: () => this.undoTransaction(transactions),
      redo: () => this.redoTransaction(transactions),
      description,
    });

    this._transactions = null;
  }

  /**
   * Get the visible range of the sheet
   * @param startX The start x position in pixels
   * @param endX The end x position in pixels
   * @param startY The start y position in pixels
   * @param endY The end y position in pixels
   * @returns The visible range in cell positions
   */
  public getVisibleRange(
    startX: number,
    endX: number,
    startY: number,
    endY: number,
  ): {
    startCol: { index: number; accum: number };
    endCol: { index: number; accum: number };
    startRow: { index: number; accum: number };
    endRow: { index: number; accum: number };
  } {
    const startCol = this.getColIndexAt(startX);
    const endCol = this.getColIndexAt(endX);
    const startRow = this.getRowIndexAt(startY);
    const endRow = this.getRowIndexAt(endY);

    return {
      startCol,
      endCol,
      startRow,
      endRow,
    };
  }
}
